/**
 * QuillActionConfirm — shared production-write confirmation dialog for all
 * Quill admin pages.
 *
 * The design-system `components/help/ActionConfirm` is registry-driven: it
 * looks up copy by `actionId` from `src/data/help-content.json` and silently
 * renders nothing (`return null`) if the id isn't present. Every Quill
 * mutation dialog needs bespoke, per-action copy (slot name, member counts,
 * old/new values, an explicit "this is production" warning) that isn't a
 * good fit for that static registry, so this component takes its copy
 * directly via props instead of an actionId — mirroring ActionConfirm's
 * visual language (warning icon, Playfair Display title, burgundy/gold
 * button theming, Cancel/Confirm footer) but always rendering.
 *
 * This replaces five near-identical local dialogs that were independently
 * built per Quill page directory (models/ProductionActionConfirm,
 * guardrails/GuardrailActionConfirm, reasoning/ReasoningActionConfirm,
 * weights/WeightsActionConfirm, interview/InterviewActionConfirm) during
 * the initial build, back when each build was scoped to its own directory.
 * This is the single superset version — every prop any of the five used is
 * supported here.
 *
 * Every Quill production write (model default changes/rollback, guardrail
 * tier/rule/template/override changes, reasoning version save/restore,
 * weight matrix activate/rollback, interview question create/save/archive)
 * routes through this with explicit "production" copy — these mutations hit
 * the live Quill DB and affect real members immediately.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { colors, typography } from '../../../theme/designTokens';

export interface QuillActionConfirmProps {
  open: boolean;
  /** Dialog title, e.g. "Save tier changes". */
  title: string;
  /** Primary body copy — explicit production-consequence explanation. */
  message: React.ReactNode;
  /** Optional secondary line rendered directly under the message (unboxed). */
  detail?: React.ReactNode;
  /**
   * Optional persistent note rendered in a warning-tinted box, for a caveat
   * that should stay visually distinct from the main message (e.g. a
   * cache-invalidation delay on rollback).
   */
  caption?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Stronger (burgundy) visual treatment for wider-blast-radius actions
   * (e.g. rollback, discard, archive). Still one-click — nothing here is a
   * delete.
   */
  destructive?: boolean;
  /** True while the confirm action is in flight. Disables both buttons and blocks close-on-backdrop. */
  loading?: boolean;
  /** Confirm-button label shown while `loading` is true. */
  pendingLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const QuillActionConfirm: React.FC<QuillActionConfirmProps> = ({
  open,
  title,
  message,
  detail,
  caption,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  pendingLabel = 'Working…',
  onConfirm,
  onCancel,
}) => {
  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: typography.fonts.display,
          color: destructive ? colors.error.dark : 'text.primary',
        }}
      >
        <WarningAmberIcon sx={{ color: destructive ? colors.error.main : colors.warning.main }} />
        {title}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
          {message}
        </Typography>

        {detail && <Box sx={{ mt: 1.5 }}>{detail}</Box>}

        {caption && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: '8px',
              backgroundColor: colors.warning[50],
              border: `1px solid ${colors.warning.light}`,
            }}
          >
            <Typography variant="body2" sx={{ color: colors.warning.dark }}>
              {caption}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            backgroundColor: destructive ? colors.error.main : colors.warning.main,
            color: destructive ? '#FFFFFF' : '#1B1F23',
            '&:hover': {
              backgroundColor: destructive ? colors.error.dark : colors.warning.dark,
            },
          }}
        >
          {loading ? pendingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuillActionConfirm;
